import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { OperationsApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import {
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
  FilterKeyword,
  EuqFeedbackContentReqDto,
  EuqTimeRangeEnum,
  ServiceTypeFilter,
} from '../../type';
import { RequestSourceFilter } from './useFilters';

interface EuqFeedbackQueryParams {
  status: FeedbackFilterStatus;
  category: FeedbackFilterCategory;
  resultCategory: FeedbackResultCategory;
  requestSource: RequestSourceFilter;
  timeRange: EuqTimeRangeEnum;
  keyword: FilterKeyword;
  pageIndex: number;
  pageSize: number;
  sortKey: string;
  orgId: string;
  mitigationStatus: number | null;
  serviceType?: ServiceTypeFilter;
}

export type EuqFeedbackFilterParams = Omit<
  EuqFeedbackQueryParams,
  'pageIndex' | 'pageSize' | 'orgId'
>;

export const buildEuqFeedbackFilterParams = (
  params: EuqFeedbackFilterParams
): Omit<EuqFeedbackContentReqDto, 'offset' | 'limit'> => {
  const {
    sortKey,
    keyword,
    status,
    category,
    resultCategory,
    requestSource,
    timeRange,
    mitigationStatus,
    serviceType,
  } = params;

  let sortParams: {
    sort: string;
    order: 'asc' | 'desc';
  };

  if (sortKey.indexOf('-') === 0) {
    sortParams = {
      sort: sortKey.replace('-', ''),
      order: 'desc',
    };
  } else {
    sortParams = {
      sort: sortKey,
      order: 'asc',
    };
  }

  return {
    status,
    category,
    result_category: resultCategory,
    request_source: requestSource === 'all' ? undefined : requestSource,
    predefined: timeRange,
    ...sortParams,
    message_recipients: keyword.recipient,
    requested_by: keyword.reporter,
    message_subject: keyword.subject,
    message_sender: keyword.sender,
    mitigation_status: mitigationStatus,
    ...(serviceType &&
      serviceType !== ServiceTypeFilter.All && {
        service_type: serviceType,
      }),
  };
};

const getEuqFeedbackQueryKey = (params: EuqFeedbackQueryParams) => [
  'euqFeedback',
  params.orgId,
  params.status,
  params.category,
  params.resultCategory,
  params.requestSource,
  params.timeRange,
  params.keyword,
  params.pageIndex,
  params.pageSize,
  params.sortKey,
  params.mitigationStatus,
  params.serviceType,
];

export const useEuqFeedbackQuery = (params: EuqFeedbackQueryParams) => {
  const { t } = useTranslation();

  const queryKey = getEuqFeedbackQueryKey(params);

  return useQuery({
    queryKey,
    queryFn: async () => {
      const requestParams: EuqFeedbackContentReqDto = {
        ...buildEuqFeedbackFilterParams(params),
        offset: params.pageSize * params.pageIndex,
        limit: params.pageSize,
      };

      const response = await OperationsApi.apiGetEuqContentList(requestParams);
      return response.data;
    },
    enabled: !!params.orgId,
    placeholderData: keepPreviousData,
    meta: {
      errorNotification: {
        title: t('common.apiException.internalError'),
      },
    },
  });
};

export const useManualApproveConfigQuery = (orgId: string) => {
  return useQuery({
    queryKey: ['manualApproveConfig', orgId],
    queryFn: () => OperationsApi.apiGetManualApproveConfig(orgId),
    enabled: !!orgId,
    select: (response) => response.data,
  });
};
